import {
  keepPreviousData,
  queryOptions,
  skipToken,
} from '@tanstack/react-query';

import type {
  OnlineBookingEventDetail,
  OnlineBookingEventsResponse,
  OnlineBookingGroupSessionsQueryParams,
  OnlineBookingGroupSessionsResponse,
  OnlineBookingPaymentOptions,
  OnlineBookingPaymentOptionsQueryParams,
  OnlineBookingReservationsQueryParams,
  OnlineBookingReservationsResponse,
  OnlineBookingServicesResponse,
  OnlineBookingSpecialistsResponse,
} from '@/types/api/queries';

import { HttpClient } from '@/network/HttpClient';

export const onlineBookingKeys = {
  all: () => ['onlineBooking'] as const,
  services: (url: string, locationId: number | null) =>
    [...onlineBookingKeys.all(), 'services', url, locationId] as const,
  specialists: (
    url: string,
    locationId: number | null,
    serviceId: number | null,
  ) =>
    [
      ...onlineBookingKeys.all(),
      'specialists',
      url,
      locationId,
      serviceId,
    ] as const,
  groupSessions: (props: OnlineBookingGroupSessionsQueryParams) =>
    [
      ...onlineBookingKeys.all(),
      'groupSessions',
      props.url,
      props.locationId,
      props.from,
      props.till,
      props.sizePerPage,
    ] as const,
  events: (url: string) => [...onlineBookingKeys.all(), 'events', url] as const,
  event: (url: string, eventId: number | null) =>
    [...onlineBookingKeys.all(), 'event', url, eventId] as const,
  paymentOptions: (props: OnlineBookingPaymentOptionsQueryParams) =>
    [
      ...onlineBookingKeys.all(),
      'paymentOptions',
      props.url,
      props.groupSessionId,
      props.eventId,
      props.eventWaveId,
      props.serviceId,
      props.staffId,
      props.locationId,
      props.startsAt,
    ] as const,
  reservations: (url: string, params: OnlineBookingReservationsQueryParams) =>
    [...onlineBookingKeys.all(), 'reservations', url, params] as const,
};

export const onlineBookingQueries = {
  services: (url: string, locationId: number | null) =>
    queryOptions({
      queryKey: onlineBookingKeys.services(url, locationId),
      queryFn:
        url && locationId
          ? () =>
              HttpClient.request<OnlineBookingServicesResponse>({
                method: 'GET',
                url,
                params: { location_id: locationId },
              })
          : skipToken,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    }),

  specialists: (
    url: string,
    locationId: number | null,
    serviceId: number | null,
  ) =>
    queryOptions({
      queryKey: onlineBookingKeys.specialists(url, locationId, serviceId),
      queryFn:
        url && locationId && serviceId
          ? () =>
              HttpClient.request<OnlineBookingSpecialistsResponse>({
                method: 'GET',
                url,
                params: { location_id: locationId, service_id: serviceId },
              })
          : skipToken,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    }),

  groupSessions: (props: OnlineBookingGroupSessionsQueryParams) => {
    const { url, locationId, from, till, sizePerPage } = props;

    return queryOptions({
      queryKey: onlineBookingKeys.groupSessions(props),
      queryFn:
        url && locationId
          ? () =>
              HttpClient.request<OnlineBookingGroupSessionsResponse>({
                method: 'GET',
                url,
                params: {
                  location_id: locationId,
                  size_per_page: sizePerPage,
                  filters: { period: { from, till } },
                },
              })
          : skipToken,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    });
  },

  // The booking horizon deliberately does not apply — an event's date is the gym's own choice.
  events: (url: string, isEnabled: boolean) =>
    queryOptions({
      queryKey: onlineBookingKeys.events(url),
      queryFn:
        url && isEnabled
          ? () =>
              HttpClient.request<OnlineBookingEventsResponse>({
                method: 'GET',
                url,
              })
          : skipToken,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    }),

  event: (url: string, eventId: number | null) =>
    queryOptions({
      queryKey: onlineBookingKeys.event(url, eventId),
      queryFn:
        url && eventId
          ? () =>
              HttpClient.request<OnlineBookingEventDetail>({
                method: 'GET',
                url: `${url}/${eventId}`,
              })
          : skipToken,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    }),

  paymentOptions: (props: OnlineBookingPaymentOptionsQueryParams) => {
    const {
      url,
      isEnabled,
      groupSessionId,
      eventId,
      eventWaveId,
      serviceId,
      staffId,
      locationId,
      startsAt,
    } = props;

    const personalParams =
      serviceId && staffId && locationId && startsAt
        ? {
            service_id: serviceId,
            staff_id: staffId,
            location_id: locationId,
            starts_at: startsAt,
          }
        : null;
    const eventParams = eventId
      ? { event_id: eventId, event_wave_id: eventWaveId }
      : null;
    const groupParams = groupSessionId
      ? { group_session_id: groupSessionId }
      : null;
    const params = eventParams ?? groupParams ?? personalParams;

    return queryOptions({
      queryKey: onlineBookingKeys.paymentOptions(props),
      queryFn:
        isEnabled && url && params
          ? () =>
              HttpClient.request<OnlineBookingPaymentOptions>({
                method: 'GET',
                url,
                params,
              })
          : skipToken,
      refetchOnWindowFocus: false,
      gcTime: 0,
      staleTime: 0,
      retry: false,
    });
  },

  reservations: (url: string, params: OnlineBookingReservationsQueryParams) =>
    queryOptions({
      queryKey: onlineBookingKeys.reservations(url, params),
      queryFn: url
        ? () =>
            HttpClient.request<OnlineBookingReservationsResponse>({
              method: 'GET',
              url,
              params,
            })
        : skipToken,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    }),
};
